import React from 'react';
import { useTranslation } from 'localization';
import { ItemSyndicationInfo } from 'types/channel';
import './SyndicationSourceInfoMolecule.scss';

export interface SyndicationSourceInfoMoleculeProps {
  info: ItemSyndicationInfo;
  className?: string;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

const SyndicationSourceInfoMolecule: React.FC<SyndicationSourceInfoMoleculeProps> = ({
  info,
  className = '',
}) => {
  const { t } = useTranslation();

  if (!info?.isSyndicated) {
    return null;
  }

  const classes = ['marketplace-syndication-source-info-molecule', className]
    .filter(Boolean)
    .join(' ');

  const sourceLabel = info.sourceChannelName || info.sourceChannelSystemName || '';
  const hasSource = sourceLabel.length > 0;

  const versionModeLabel = (() => {
    if (info.versionMode === 'pinned' && info.pinnedVersion != null) {
      return t('marketplace.itemDetail.details.versionModePinned', {
        version: info.pinnedVersion,
      });
    }
    if (info.versionMode === 'latest') return t('marketplace.itemDetail.details.versionModeLatest');
    return null;
  })();

  return (
    <section
      className={classes}
      data-testid="syndication-source-info"
    >
      <h4 className="marketplace-syndication-source-info-molecule__title">
        <i className="pi pi-share-alt" aria-hidden="true" />
        {t('marketplace.itemDetail.details.syndicationSource')}
      </h4>

      {hasSource ? (
        <p
          className="marketplace-syndication-source-info-molecule__source"
          data-testid="syndication-source-name"
        >
          {sourceLabel}
        </p>
      ) : (
        <p
          className="marketplace-syndication-source-info-molecule__source marketplace-syndication-source-info-molecule__source--unavailable"
          data-testid="syndication-source-unavailable"
        >
          {t('marketplace.itemDetail.details.sourceUnavailable')}
        </p>
      )}

      {versionModeLabel && (
        <div className="marketplace-syndication-source-info-molecule__row">
          <span className="marketplace-syndication-source-info-molecule__label">
            {t('marketplace.itemDetail.details.versionMode')}
          </span>
          <span
            className="marketplace-syndication-source-info-molecule__value"
            data-testid="syndication-version-mode"
          >
            {versionModeLabel}
          </span>
        </div>
      )}

      {info.resolvedVersion != null && (
        <div className="marketplace-syndication-source-info-molecule__row">
          <span
            className="marketplace-syndication-source-info-molecule__value"
            data-testid="syndication-resolved-version"
          >
            {t('marketplace.itemDetail.details.resolvedVersion', {
              version: info.resolvedVersion,
            })}
          </span>
        </div>
      )}

      {info.lastSyncedAt && (
        <div className="marketplace-syndication-source-info-molecule__row">
          <span
            className="marketplace-syndication-source-info-molecule__value"
            data-testid="syndication-last-synced"
          >
            {t('marketplace.itemDetail.details.lastSynced', {
              date: formatDate(info.lastSyncedAt),
            })}
          </span>
        </div>
      )}
    </section>
  );
};

export default SyndicationSourceInfoMolecule;
